import { HyperLinkRedirectControl } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { EnvironmentTax } from "@/Common/Helpers/EnvironmentTax";
import { RedirectTo } from "@/Common/Models/UI/Enums";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import { TableDataSourceItem } from "@/Common/Models/UI/Tables/ITableDataSourceItem";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { PayableInboxItem } from "../Api/Inbox/Dto/Response/BillingInboxItem";

class PayableInboxTableHelper {
    public saveOptionKey: string = "PayableTable";
    private columnOptions: Immutable.List<MyleTableColumnOptions>;

    constructor() {
        this.updateColumnsOption();
    }

    public getTableColumns(): Immutable.List<MyleTableColumn> {
        return this.columnOptions
            .map(columnOption => this.buildTableColumn(columnOption))
            .toList();
    }

    public getTableRenderOptions(): ITableRenderOptions {
        const defaultTablePluginOptions = {
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: this.columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: false,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        };

        return Immutable.fromJS(defaultTablePluginOptions);
    }

    public updateColumnsOption(): void {
        this.columnOptions = this.getColumnsOptions();
    }

    public getColumnOptionsForOrderColumnsPopup(): ColumnConfigurationModel[] {
        const columnsOptions = this.getTableColumns().map(tableColums => tableColums.columnOptions).toArray();
        return cacheStore.Helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions);
    }

    private buildTableColumn(option: MyleTableColumnOptions): MyleTableColumn {
        const column = new MyleTableColumn(option);
        switch (option.FieldName) {
            case "Patient":
                column.renderCell = (item: TableDataSourceItem<PayableInboxItem>) => this.renderPatientNameAsLinkToPatientFile(item);
                break;

            default:
                column.renderCell = (item: TableDataSourceItem<PayableInboxItem>) => this.renderTextCell(option.FieldName, item);
                break;
        }
        return column;
    }

    private renderTextCell(fieldName: string, item: TableDataSourceItem<PayableInboxItem>): JSX.Element {
        const text = this.formatCellText(fieldName, item);

        return <div>{text}</div>;
    }

    private formatCellText(fieldName: string, item: TableDataSourceItem<PayableInboxItem>): string {
        switch (fieldName) {
            case "CreationDate":
                return this.formatDate(item.Model.CreationDate).toString();
            case "VisitDate":
                return this.formatDate(item.Model.VisitDate).toString();
            default:
                return item.Model[fieldName];
        }
    }

    private getColumnsOptions(): Immutable.List<MyleTableColumnOptions> {
        return cacheStore.Helper.loadTableColumnOptionsFromSettings(this.saveOptionKey, this.getColumnsDefaultOptions());
    }

    private getColumnsDefaultOptions(): MyleTableColumnOptions[] {
        return [
            this.getCommonTableColumnOptions(cacheStore.GetString("CreationDateLabel"), "CreationDate", 6),
            this.getCommonTableColumnOptions(cacheStore.GetString("VisitDateLabel"), "VisitDate", 6),
            this.getCommonTableColumnOptions(cacheStore.GetString("NumberLabel"), "Number", 6),
            this.getCommonTableColumnOptions(cacheStore.GetString("ProviderLabel"), "Provider", 6),
            this.getCommonTableColumnOptions(cacheStore.GetString("SiteDLabel"), "Site", 6),
            this.getCommonTableColumnOptions(cacheStore.GetString("PatientLabel"), "Patient", 6),
            this.getCommonTableColumnOptions(sessionStore.getDefaultHealthCardLabel(), "HealthCard", 6),
            this.getCommonTableColumnOptions(cacheStore.GetString("BilledByLabel"), "BilledBy", 6),
            this.getCommonTableColumnOptions(cacheStore.GetString("BilledToLabel"), "BilledTo", 6),
            this.getCommonTableColumnOptions(cacheStore.GetString("SubtotalLabel"), "SubTotal", 6),
            this.getCommonTableColumnOptions(cacheStore.GetString("DiscountLabel"), "Discount", 6),
            this.getCommonTableColumnOptions(EnvironmentTax.getPstOrQstLabel(), "GST", 6),
            this.getCommonTableColumnOptions(cacheStore.GetString("QstMLabel"), "QST", 6),
            this.getCommonTableColumnOptions(cacheStore.GetString("TotalDueLabel"), "TotalDue", 6)
        ];
    }

    private getCommonTableColumnOptions(headerText: string, fieldName: string, WidthPercentage: number): MyleTableColumnOptions {
        return new MyleTableColumnOptions({
            HeaderText: headerText,
            FieldName: fieldName,
            RowClickEnabled: false,
            WidthPercentageDefault: WidthPercentage
        });
    }

    private renderPatientNameAsLinkToPatientFile(item: TableDataSourceItem<PayableInboxItem>): JSX.Element {
        return (
            <HyperLinkRedirectControl
                target={RedirectTo.Patient}
                redirectValue={item.Model.PatientContactId}
                text={`${item.Model.Patient}`}
            />
        );
    }

    private formatDate(date?: Date): string {
        if (!date)
            return "";

        const parsedTime = LuxonDateTimeParser.parse(date);
        if (!parsedTime.isValid)
            return "";

        return parsedTime
            .toLocal()
            .toFormat("yyyy/LL/dd");
    }
}

export { PayableInboxTableHelper };
